import React, { useEffect, useState, useRef } from "react";
import { api, apiError, formatPhone, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AlumniLayout from "@/components/AlumniLayout";
import BiometricsCard from "@/components/BiometricsCard";
import { Save, Plus, Trash2, Linkedin, Facebook, Twitter, Instagram, Globe, KeyRound, Upload } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useUnsavedChanges } from "@/lib/useUnsavedChanges";

const uid = () => Math.random().toString(36).slice(2);
const SOCIALS = [["linkedin", "LinkedIn", Linkedin], ["facebook", "Facebook", Facebook], ["twitter", "X / Twitter", Twitter], ["instagram", "Instagram", Instagram], ["website", "Website", Globe]];

export default function MyProfile() {
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [optReason, setOptReason] = useState("");

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo", data.path);
      toast.success("Photo uploaded — click Save My Profile to keep it");
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const setPrimary = (key, idx) => setF((p) => ({ ...p, [key]: (p[key] || []).map((it, j) => ({ ...it, primary: j === idx })) }));

  const savedSnapshot = useRef("");
  useEffect(() => {
    api.get("/me/profile").then((r) => {
      const loaded = { ...r.data, class_year: r.data.class_year ?? "", year_graduated: r.data.year_graduated ?? "", year_entered: r.data.year_entered ?? "", birthdate: r.data.birthdate ?? "", bio: r.data.bio ?? "", social: r.data.social || {}, emails: r.data.emails || [], phones: r.data.phones || [], addresses: r.data.addresses || [], employments: r.data.employments || [], degrees: r.data.degrees || [], family: r.data.family || [], interests: r.data.interests || [] };
      setF(loaded);
      savedSnapshot.current = JSON.stringify(loaded);
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const dirty = !!(f && savedSnapshot.current) && JSON.stringify(f) !== savedSnapshot.current;
  useUnsavedChanges(dirty);

  if (!f) return <AlumniLayout><div className="text-muted-foreground">Loading your profile…</div></AlumniLayout>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setF((p) => ({ ...p, social: { ...p.social, [k]: v } }));
  const upd = (key, i, patch) => setF((p) => ({ ...p, [key]: p[key].map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const add = (key, obj) => setF((p) => ({ ...p, [key]: [...p[key], { id: uid(), primary: p[key].length === 0, ...obj }] }));
  const del = (key, i) => setF((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, class_year: f.class_year ? parseInt(f.class_year) : null, year_graduated: f.year_graduated ? parseInt(f.year_graduated) : null, year_entered: f.year_entered ? parseInt(f.year_entered) : null };
      await api.put("/me/profile", payload);
      savedSnapshot.current = JSON.stringify(f);
      toast.success("Your profile has been updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updateEmailPrefs = async (patch) => {
    try {
      const body = { ...patch };
      const optingOut = patch.opt_out_all === true || patch.opt_out_marketing === true || patch.opt_out_solicitations === true;
      if (optingOut && optReason.trim()) body.reason = optReason.trim();
      const { data } = await api.patch("/me/email-preferences", body);
      setF((p) => ({ ...p, email_opt_out: data.email_opt_out, email_prefs: data.email_prefs }));
      try { const snap = JSON.parse(savedSnapshot.current || "{}"); snap.email_opt_out = data.email_opt_out; snap.email_prefs = data.email_prefs; savedSnapshot.current = JSON.stringify(snap); } catch {}
      toast.success("Your email preferences were updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const emp = f.employments[0] || {};

  return (
    <AlumniLayout>
      <h1 className="font-serif text-3xl tracking-tight">Welcome, {user?.name}</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Keep your information current so classmates and the university can stay in touch.</p>

      <Card title="Basic Information">
        <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border">
          {f.photo ? <img src={fileUrl(f.photo)} alt="" className="h-20 w-20 rounded-full object-cover ring-2 ring-muted" /> : <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">No photo</div>}
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-muted text-sm">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : (f.photo ? "Replace Photo" : "Upload Photo")}
            <input data-testid="me-photo-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          </label>
          {f.photo && <button type="button" data-testid="me-photo-remove" onClick={() => { set("photo", ""); toast.success("Photo removed — click Save to apply"); }} className="flex items-center gap-2 px-3 py-2 rounded-md border border-border text-sm text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="h-4 w-4" /> Remove</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Prefix</label>
            <select data-testid="me-field-prefix" value={f.prefix || ""} onChange={(e) => set("prefix", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              {["", "Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Rev.", "Hon."].map((s) => <option key={s} value={s}>{s || "— None —"}</option>)}
            </select>
          </div>
          <Field label="First Name" v={f.first_name} on={(v) => set("first_name", v)} t="first_name" />
          <Field label="Middle Name" v={f.middle_name || ""} on={(v) => set("middle_name", v)} t="middle_name" />
          <Field label="Last Name" v={f.last_name} on={(v) => set("last_name", v)} t="last_name" />
          <div>
            <label className="block text-sm font-medium mb-1">Suffix</label>
            <select data-testid="me-field-suffix" value={f.suffix || ""} onChange={(e) => set("suffix", e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none">
              {["", "Jr.", "Sr.", "II", "III", "IV", "V"].map((s) => <option key={s} value={s}>{s || "— None —"}</option>)}
            </select>
          </div>
          <Field label="Preferred Name" v={f.preferred_name} on={(v) => set("preferred_name", v)} t="preferred_name" />
          <Field label="Former / Maiden Name" v={f.former_name || ""} on={(v) => set("former_name", v)} t="former_name" />
          <Field label="Addressee (how mail is addressed)" v={f.addressee || ""} on={(v) => set("addressee", v)} t="addressee" />
          <Field label="Salutation (greeting)" v={f.salutation || ""} on={(v) => set("salutation", v)} t="salutation" />
          <Field label="Class Year" v={f.class_year} on={(v) => set("class_year", v)} t="class_year" type="number" />
          <Field label="Birthdate" v={f.birthdate || ""} on={(v) => set("birthdate", v)} t="birthdate" type="date" />
          <Field label="Year Entered" v={f.year_entered || ""} on={(v) => set("year_entered", v)} t="year_entered" type="number" />
          <Field label="Year Graduated" v={f.year_graduated || ""} on={(v) => set("year_graduated", v)} t="year_graduated" type="number" />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">Bio</label>
          <textarea data-testid="me-field-bio" value={f.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={3} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" placeholder="Tell your fellow Crusaders what you've been up to…" />
        </div>
      </Card>

      <Card title="Education">
        <List label="Degrees" items={f.degrees} onAdd={() => add("degrees", { grad_year: null, degree: "", major: "", minor: "" })} onDel={(i) => del("degrees", i)}
          render={(it, i) => (
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input data-testid={`me-degree-year-${i}`} type="number" value={it.grad_year || ""} onChange={(e) => upd("degrees", i, { grad_year: e.target.value ? parseInt(e.target.value) : null })} placeholder="Grad Year" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input data-testid={`me-degree-name-${i}`} value={it.degree || ""} onChange={(e) => upd("degrees", i, { degree: e.target.value })} placeholder="Degree (BA, MBA)" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input data-testid={`me-degree-major-${i}`} value={it.major || ""} onChange={(e) => upd("degrees", i, { major: e.target.value })} placeholder="Major" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input data-testid={`me-degree-minor-${i}`} value={it.minor || ""} onChange={(e) => upd("degrees", i, { minor: e.target.value })} placeholder="Minor" className="px-3 py-2 rounded-md border border-input bg-background" />
            </div>
          )} />
      </Card>

      <Card title="Contact">
        <List label="Emails" items={f.emails} onAdd={() => add("emails", { value: "" })} onDel={(i) => del("emails", i)}
          render={(it, i) => <input data-testid={`me-email-${i}`} value={it.value} onChange={(e) => upd("emails", i, { value: e.target.value })} placeholder="email@example.com" className="flex-1 px-3 py-2 rounded-md border border-input bg-background" />} />
        <List label="Phones" items={f.phones} onAdd={() => add("phones", { value: "" })} onDel={(i) => del("phones", i)}
          render={(it, i) => (
            <div className="flex-1 flex items-center gap-2">
              <input data-testid={`me-phone-${i}`} value={it.value} onChange={(e) => upd("phones", i, { value: e.target.value })} onBlur={(e) => upd("phones", i, { value: formatPhone(e.target.value) })} placeholder="(555) 000-0000" className="flex-1 px-3 py-2 rounded-md border border-input bg-background" />
              <select data-testid={`me-phone-type-${i}`} value={it.type || "mobile"} onChange={(e) => upd("phones", i, { type: e.target.value })} className="shrink-0 px-2 py-2 rounded-md border border-input bg-background text-sm">
                <option value="mobile">Mobile</option><option value="home">Home</option><option value="work">Work</option><option value="other">Other</option>
              </select>
              <button type="button" data-testid={`me-phone-primary-${i}`} onClick={() => setPrimary("phones", i)} className={`shrink-0 text-[11px] px-2 py-1.5 rounded-md border transition-colors ${it.primary ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-muted"}`}>{it.primary ? "Primary" : "Set primary"}</button>
            </div>
          )} />
        <List label="Addresses" items={f.addresses} onAdd={() => add("addresses", { line1: "", city: "", state: "", postal_code: "", country: "" })} onDel={(i) => del("addresses", i)}
          render={(it, i) => (
            <div className="flex-1 grid grid-cols-2 gap-2">
              <input value={it.line1 || ""} onChange={(e) => upd("addresses", i, { line1: e.target.value })} placeholder="Street" className="col-span-2 px-3 py-2 rounded-md border border-input bg-background" />
              <input value={it.city || ""} onChange={(e) => upd("addresses", i, { city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input value={it.state || ""} onChange={(e) => upd("addresses", i, { state: e.target.value })} placeholder="State" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input value={it.postal_code || ""} onChange={(e) => upd("addresses", i, { postal_code: e.target.value })} placeholder="ZIP / Postal code" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input value={it.country || ""} onChange={(e) => upd("addresses", i, { country: e.target.value })} placeholder="Country" className="px-3 py-2 rounded-md border border-input bg-background" />
              <select data-testid={`me-address-type-${i}`} value={it.type || "home"} onChange={(e) => upd("addresses", i, { type: e.target.value })} className="px-2 py-2 rounded-md border border-input bg-background text-sm">
                <option value="home">Home</option><option value="work">Work</option><option value="seasonal">Seasonal</option><option value="other">Other</option>
              </select>
              <button type="button" data-testid={`me-address-primary-${i}`} onClick={() => setPrimary("addresses", i)} className={`justify-self-start text-[11px] px-2 py-1.5 rounded-md border transition-colors ${it.primary ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-muted"}`}>{it.primary ? "Primary address" : "Set as primary address"}</button>
            </div>
          )} />
      </Card>

      <Card title="Employment">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="me-employer" value={emp.employer || ""} onChange={(e) => f.employments.length ? upd("employments", 0, { employer: e.target.value }) : add("employments", { employer: e.target.value })} placeholder="Employer" className="px-3 py-2 rounded-md border border-input bg-background" />
          <input data-testid="me-title" value={emp.title || ""} onChange={(e) => f.employments.length ? upd("employments", 0, { title: e.target.value }) : add("employments", { title: e.target.value })} placeholder="Job Title" className="px-3 py-2 rounded-md border border-input bg-background" />
          <input data-testid="me-industry" value={emp.industry || ""} onChange={(e) => f.employments.length ? upd("employments", 0, { industry: e.target.value }) : add("employments", { industry: e.target.value })} placeholder="Industry" className="px-3 py-2 rounded-md border border-input bg-background" />
        </div>
      </Card>

      <Card title="Family">
        <p className="text-sm text-muted-foreground mb-3 -mt-2">Add your spouse, children, or other family connected to the university.</p>
        <List label="Family Members" items={f.family} onAdd={() => add("family", { relationship: "Spouse", name: "", notes: "" })} onDel={(i) => del("family", i)}
          render={(it, i) => (
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select data-testid={`me-family-rel-${i}`} value={it.relationship || "Spouse"} onChange={(e) => upd("family", i, { relationship: e.target.value })} className="px-3 py-2 rounded-md border border-input bg-background">
                {["Spouse", "Partner", "Child", "Parent", "Sibling", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input data-testid={`me-family-name-${i}`} value={it.name || ""} onChange={(e) => upd("family", i, { name: e.target.value })} placeholder="Name" className="px-3 py-2 rounded-md border border-input bg-background" />
              <input data-testid={`me-family-notes-${i}`} value={it.notes || ""} onChange={(e) => upd("family", i, { notes: e.target.value })} placeholder="Notes (e.g., Class of '02)" className="px-3 py-2 rounded-md border border-input bg-background" />
            </div>
          )} />
      </Card>

      <Card title="Social & Interests">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SOCIALS.map(([k, label, Icon]) => (
            <div key={k}>
              <label className="flex items-center gap-2 text-sm font-medium mb-1"><Icon className="h-4 w-4 text-muted-foreground" /> {label}</label>
              <input data-testid={`me-social-${k}`} value={f.social[k] || ""} onChange={(e) => setSocial(k, e.target.value)} placeholder="https://…" className="w-full px-3 py-2 rounded-md border border-input bg-background" />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1">Interests <span className="text-muted-foreground font-normal">(comma separated)</span></label>
          <input data-testid="me-interests" value={f.interests.join(", ")} onChange={(e) => set("interests", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="w-full px-3 py-2 rounded-md border border-input bg-background" />
        </div>
      </Card>

      <div className="flex justify-end">
        <button data-testid="me-save" onClick={save} disabled={busy} className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save My Profile"}
        </button>
      </div>

      <Card title="Email Preferences">
        <p className="text-sm text-muted-foreground -mt-2 mb-4">Choose which emails you'd like to receive from the alumni office. Changes save immediately.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Opt out of all email</div>
              <div className="text-xs text-muted-foreground">Stop receiving every email, including event invitations.</div>
            </div>
            <Switch data-testid="me-opt-out-all" checked={!!f.email_opt_out} onCheckedChange={(v) => updateEmailPrefs({ opt_out_all: v })} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Opt out of marketing email</div>
              <div className="text-xs text-muted-foreground">Newsletters &amp; announcements only — you'll still receive event invitations.</div>
            </div>
            <Switch data-testid="me-opt-out-marketing" checked={!!f.email_opt_out || (f.email_prefs && f.email_prefs.announcements === false)} disabled={!!f.email_opt_out} onCheckedChange={(v) => updateEmailPrefs({ opt_out_marketing: v })} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Opt out of financial &amp; giving email</div>
              <div className="text-xs text-muted-foreground">Fundraising appeals and giving solicitations — other emails are unaffected.</div>
            </div>
            <Switch data-testid="me-opt-out-financial" checked={!!f.email_opt_out || (f.email_prefs && f.email_prefs.solicitations === false)} disabled={!!f.email_opt_out} onCheckedChange={(v) => updateEmailPrefs({ opt_out_solicitations: v })} />
          </div>
          <div className="pt-1">
            <label className="block text-xs text-muted-foreground mb-1">Reason for opting out (optional — helps us improve)</label>
            <input data-testid="me-opt-out-reason" value={optReason} onChange={(e) => setOptReason(e.target.value)} placeholder="e.g. Too many emails, changed interests…" className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm" />
            <p className="text-[11px] text-muted-foreground mt-1">Tip: type your reason before toggling an opt-out above and it'll be saved with your choice.</p>
          </div>
        </div>
      </Card>

      <ChangePasswordCard />
      <BiometricsCard wrapperClass="mt-8" />
    </AlumniLayout>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (nw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (nw !== nw2) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Your password has been changed");
      setCur(""); setNw(""); setNw2("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-5 mt-8">
      <h2 className="font-serif text-xl tracking-tight mb-1 flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Change Password</h2>
      <p className="text-sm text-muted-foreground mb-4">Update the password you use to sign in.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className="px-3 py-2 rounded-md border border-input bg-background" />
        <input data-testid="cp-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (8+ chars)" className="px-3 py-2 rounded-md border border-input bg-background" />
        <input data-testid="cp-confirm" type="password" value={nw2} onChange={(e) => setNw2(e.target.value)} placeholder="Confirm new password" className="px-3 py-2 rounded-md border border-input bg-background" />
      </div>
      <div className="flex justify-end mt-4">
        <button data-testid="cp-submit" onClick={submit} disabled={busy || !cur || !nw} className="px-5 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:bg-secondary/90 disabled:opacity-60">{busy ? "Updating…" : "Update Password"}</button>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-card border border-border rounded-lg shadow-sm p-5 mb-5">
    <h2 className="font-serif text-xl tracking-tight mb-4">{title}</h2>
    {children}
  </div>
);

const Field = ({ label, v, on, t, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input data-testid={`me-field-${t}`} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
  </div>
);

const List = ({ label, items, render, onAdd, onDel }) => (
  <div className="mb-4">
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.id || i} className="flex items-start gap-2">
          {render(it, i)}
          <button type="button" onClick={() => onDel(i)} className="p-2 rounded-md border border-border text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
    <button type="button" onClick={onAdd} className="mt-2 flex items-center gap-1.5 text-sm text-primary hover:underline"><Plus className="h-4 w-4" /> Add</button>
  </div>
);
